/**
 * C0-GEN Wave 3 — Editorial-monograph PART grouping.
 *
 * The option-C monograph punctuates the long report with dark feature DIVIDER
 * sheets that group the sections into three narrative Parts:
 *
 *   I   — Site & Intent      (the brief, the parcel, the question)
 *   II  — Economics & Power  (from a buildable site to a bankable plant)
 *   III — Risk & Decision    (findings, conditions, the verdict)
 *
 * This is PURE config + a lookup. A divider sheet is emitted BEFORE the first
 * rendered section that opens a new part (Parts II and III — Part I needs no
 * divider, the cover already opens it). The grouping is keyed on the canonical
 * section ids and generalises across report types: any unmapped section id
 * defaults to Part I (so a non-feasibility report simply shows fewer / no
 * part transitions rather than mis-grouping).
 *
 * GATED at the render call-site on `isEditorialLayoutsEnabled()`; this module is
 * inert config (no flag read) so it is byte-stable when the flag is OFF.
 */

export type EditorialPartId = 'I' | 'II' | 'III';

export interface EditorialPart {
  id: EditorialPartId;
  /** Roman numeral hero (e.g. "II"). */
  numeral: string;
  /** Part title (e.g. "The Economics"). */
  title: string;
  /** Italic continuation clause under the title. */
  subtitle: string;
  /** One-sentence thesis for the dark divider sheet. */
  thesis: string;
}

export const EDITORIAL_PARTS: Record<EditorialPartId, EditorialPart> = {
  I: {
    id: 'I',
    numeral: 'I',
    title: 'Site & Intent',
    subtitle: 'the brief, the parcel, and the question.',
    thesis:
      'What the proponent needs, what the land allows, and the single question the rest of this report resolves.',
  },
  II: {
    id: 'II',
    numeral: 'II',
    title: 'Economics & Power',
    subtitle: 'from a buildable site to a bankable plant.',
    thesis:
      'Whether the site can be powered, cooled, connected, and financed — the technology, grid, logistics, climate and cost that decide the return.',
  },
  III: {
    id: 'III',
    numeral: 'III',
    title: 'Risk & Decision',
    subtitle: 'findings, conditions, and the verdict.',
    thesis:
      'The confirmed risks, the gating conditions, and the recommendation the evidence supports.',
  },
};

/**
 * Canonical section-id → part. Universal sections (executiveSummary,
 * projectIntentOverview) anchor Part I; the economic/power study sections
 * anchor Part II; the risk/decision tail anchors Part III. Ids absent here
 * default to Part I (never mis-grouped). Covers the feasibility id set + the
 * common shared ids across report types.
 */
const SECTION_PART: Record<string, EditorialPartId> = {
  // Part I — Site & Intent
  executiveSummary: 'I',
  executive: 'I',
  projectIntentOverview: 'I',
  parcelOverview: 'I',
  procurementOverview: 'I',
  // Part II — Economics & Power
  btmOverview: 'II',
  gridStudy: 'II',
  gridCapacity: 'II',
  capacityCreditDeepDive: 'II',
  transportStudy: 'II',
  connectivityStudy: 'II',
  fiberRings: 'II',
  supplyAvailability: 'II',
  sitingNotes: 'II',
  climateSevereWeather: 'II',
  nepaScreening: 'II',
  radarChart: 'II',
  lcoeChart: 'II',
  // Part III — Risk & Decision
  riskAssessment: 'III',
  actionPlan: 'III',
  conclusion: 'III',
};

/** The part a section belongs to (defaults to Part I for unmapped ids). */
export function resolvePartForSection(sectionId: string): EditorialPartId {
  return SECTION_PART[sectionId] ?? 'I';
}

/** One section row for a Part-divider's mini-TOC (the option-C `.d-toc`). */
export interface EditorialPartTocEntry {
  /** Canonical section id. */
  id: string;
  /** Zero-padded display index within the FULL report order (e.g. "02"). */
  numeral: string;
  /** The section's display label (resolved by the caller). */
  label: string;
  /** True for the section the divider immediately precedes (the part opener). */
  active: boolean;
}

/**
 * Build the mini-TOC entries for a Part divider: the sections that belong to
 * `partId`, in the report's own order, each carrying its 1-based display index
 * (matching the hero-numeral the section opener shows). `labelFor` resolves the
 * display label for a section id (the caller's TOC/label resolver), and
 * `openerId` is the section the divider precedes (rendered as the active item).
 *
 * Pure + total: ids absent from the order are skipped; an empty result yields no
 * mini-TOC (the divider still renders its numeral + headline + thesis).
 */
export function buildPartTocEntries(
  partId: EditorialPartId,
  orderedSectionIds: string[],
  labelFor: (id: string) => string,
  openerId?: string,
): EditorialPartTocEntry[] {
  const entries: EditorialPartTocEntry[] = [];
  orderedSectionIds.forEach((id, index) => {
    if (resolvePartForSection(id) !== partId) return;
    entries.push({
      id,
      numeral: String(index + 1).padStart(2, '0'),
      label: labelFor(id),
      active: openerId != null && id === openerId,
    });
  });
  return entries;
}

/**
 * William QA 2026-06-28 — the full-page Part-divider sheets (I / II / III) are
 * REMOVED report-wide ("we don't need these section pages"). This now always
 * returns an empty set, so no section is ever preceded by a divider sheet. The
 * part-grouping helpers above (resolvePartForSection / buildPartTocEntries) stay
 * available for any non-divider consumer; the renderer simply no longer emits
 * divider sheets in any report type or surface.
 */
export function computePartDividerBoundaries(_orderedSectionIds: string[]): Set<string> {
  return new Set<string>();
}
